import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function AccountTypeAdmin() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("accountTypes");
  const [loading, setLoading] = useState(false);
  
  const [accountTypes, setAccountTypes] = useState([]);
  const [fdProducts, setFdProducts] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFdModal, setShowFdModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [createForm, setCreateForm] = useState({});

  const token = localStorage.getItem("token");
  const api = axios.create({
    baseURL: "/api",
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const res = await api.get("/Auth/me");
      if (!res.data.isAdmin) {
        navigate("/admin");
        return;
      }
      loadData();
    } catch (err) {
      navigate("/login");
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [typesRes, fdRes] = await Promise.all([
        api.get("/admin/AccountTypeAdmin/account-types"),
        api.get("/admin/AccountTypeAdmin/fd-products")
      ]);
      setAccountTypes(typesRes.data);
      setFdProducts(fdRes.data);
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAccountType = (type) => {
    setSelectedItem(type);
    setEditForm({ ...type });
    setShowEditModal(true);
  };

  const handleEditFdProduct = (product) => {
    setSelectedItem(product);
    setEditForm({ ...product });
    setShowFdModal(true);
  };

  const handleSaveAccountType = async () => {
    setLoading(true);
    try {
      await api.put(`/admin/AccountTypeAdmin/account-types/${selectedItem.id}`, editForm);
      alert("Account type updated successfully!");
      setShowEditModal(false);
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFdProduct = async () => {
    setLoading(true);
    try {
      if (selectedItem) {
        await api.put(`/admin/AccountTypeAdmin/fd-products/${selectedItem.id}`, editForm);
        alert("FD product updated successfully!");
      } else {
        await api.post("/admin/AccountTypeAdmin/fd-products", editForm);
        alert("FD product created successfully!");
      }
      setShowFdModal(false);
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (type, entityType) => {
    try {
      const endpoint = entityType === "account" 
        ? `/admin/AccountTypeAdmin/account-types/${type.id}/toggle-status`
        : `/admin/AccountTypeAdmin/fd-products/${type.id}/toggle-status`;
      await api.post(endpoint);
      loadData();
    } catch (err) {
      alert("Failed to toggle status");
    }
  };

  const handleApplyToExisting = async (typeId, action) => {
    if (!window.confirm(`This will update all existing accounts of this type. Continue?`)) return;
    
    try {
      const endpoint = action === "limits" 
        ? `/admin/AccountTypeAdmin/account-types/${typeId}/apply-limits`
        : `/admin/AccountTypeAdmin/account-types/${typeId}/apply-interest-rate`;
      const res = await api.post(endpoint);
      alert(res.data.message);
    } catch (err) {
      alert("Failed to apply changes");
    }
  };

  const handleCreateAccountType = async () => {
    setLoading(true);
    try {
      await api.post("/admin/AccountTypeAdmin/account-types", createForm);
      alert("Account type created successfully!");
      setShowCreateModal(false);
      setCreateForm({});
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to create");
    } finally {
      setLoading(false);
    }
  };

  const openCreateFdModal = () => {
    setSelectedItem(null);
    setEditForm({
      productName: "",
      description: "",
      tenorMonths: 12,
      interestRate: 10,
      minimumAmount: 50000,
      interestPayoutFrequency: "AtMaturity",
      allowEarlyWithdrawal: true,
      earlyWithdrawalPenalty: 50
    });
    setShowFdModal(true);
  };

  const formatCurrency = (amount) => `₦${(amount || 0).toLocaleString("en-NG")}`;

  const styles = {
    container: { padding: "20px", maxWidth: "1400px", margin: "0 auto", fontFamily: "Arial, sans-serif" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" },
    title: { fontSize: "24px", fontWeight: "bold", color: "#1a1a2e" },
    backBtn: { padding: "10px 20px", background: "#6c757d", color: "white", border: "none", borderRadius: "8px", cursor: "pointer" },
    tabs: { display: "flex", gap: "10px", marginBottom: "20px" },
    tab: { padding: "12px 24px", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600" },
    activeTab: { background: "#3b82f6", color: "white" },
    inactiveTab: { background: "#e5e7eb", color: "#374151" },
    card: { background: "white", borderRadius: "12px", padding: "20px", marginBottom: "15px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", border: "1px solid #e5e7eb" },
    cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "15px" },
    cardTitle: { fontSize: "18px", fontWeight: "bold", color: "#1a1a2e" },
    badge: { padding: "4px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: "500" },
    activeBadge: { background: "#dcfce7", color: "#16a34a" },
    inactiveBadge: { background: "#fee2e2", color: "#dc2626" },
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "15px" },
    gridItem: { padding: "12px", background: "#f9fafb", borderRadius: "8px" },
    gridLabel: { fontSize: "12px", color: "#6b7280", marginBottom: "4px" },
    gridValue: { fontSize: "16px", fontWeight: "600", color: "#1a1a2e" },
    btnRow: { display: "flex", gap: "10px", marginTop: "15px", flexWrap: "wrap" },
    btn: { padding: "8px 16px", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "500", fontSize: "13px" },
    primaryBtn: { background: "#3b82f6", color: "white" },
    successBtn: { background: "#16a34a", color: "white" },
    warningBtn: { background: "#f59e0b", color: "white" },
    dangerBtn: { background: "#dc2626", color: "white" },
    secondaryBtn: { background: "#e5e7eb", color: "#374151" },
    modalOverlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
    modal: { background: "white", borderRadius: "12px", padding: "25px", width: "800px", maxWidth: "95%", maxHeight: "90vh", overflow: "auto" },
    modalTitle: { fontSize: "20px", fontWeight: "bold", marginBottom: "20px", color: "#1a1a2e" },
    formSection: { marginBottom: "20px", padding: "15px", background: "#f9fafb", borderRadius: "8px" },
    sectionTitle: { fontSize: "14px", fontWeight: "600", color: "#374151", marginBottom: "12px", borderBottom: "1px solid #e5e7eb", paddingBottom: "8px" },
    formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" },
    formGroup: { marginBottom: "12px" },
    label: { display: "block", fontWeight: "500", marginBottom: "4px", color: "#374151", fontSize: "13px" },
    input: { width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", boxSizing: "border-box" },
    select: { width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", boxSizing: "border-box", background: "white" },
    checkbox: { display: "flex", alignItems: "center", gap: "8px", padding: "8px", background: "white", borderRadius: "6px", cursor: "pointer" },
    modalFooter: { display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "20px", paddingTop: "15px", borderTop: "1px solid #e5e7eb" },
    statsRow: { display: "flex", gap: "15px", marginTop: "10px" },
    stat: { padding: "8px 12px", background: "#eff6ff", borderRadius: "6px", fontSize: "13px" }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>⚙️ Account Type Configuration</h1>
        <button style={styles.backBtn} onClick={() => navigate("/admin")}>← Back to Admin</button>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(activeTab === "accountTypes" ? styles.activeTab : styles.inactiveTab) }}
          onClick={() => setActiveTab("accountTypes")}
        >
          🏦 Account Types
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === "fdProducts" ? styles.activeTab : styles.inactiveTab) }}
          onClick={() => setActiveTab("fdProducts")}
        >
          🔒 Fixed Deposit Products
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : activeTab === "accountTypes" ? (
        <>
          <div style={{ marginBottom: "15px" }}>
            <button style={{ ...styles.btn, ...styles.successBtn }} onClick={() => {
              setCreateForm({ accountType: "", displayName: "", minimumOpeningBalance: 0, minimumBalance: 0, defaultDailyLimit: 1000000, baseInterestRate: 0 });
              setShowCreateModal(true);
            }}>
              ➕ Create New Account Type
            </button>
          </div>

          {accountTypes.map(type => (
            <div key={type.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <div>
                  <div style={styles.cardTitle}>{type.displayName}</div>
                  <div style={{ fontSize: "13px", color: "#6b7280" }}>{type.description}</div>
                  <div style={styles.statsRow}>
                    <span style={styles.stat}>📊 {type.totalAccounts} accounts</span>
                    <span style={styles.stat}>💰 {formatCurrency(type.totalBalance)} total</span>
                  </div>
                </div>
                <span style={{ ...styles.badge, ...(type.isActive ? styles.activeBadge : styles.inactiveBadge) }}>
                  {type.isActive ? "Active" : "Inactive"}
                </span>
              </div>

              <div style={styles.grid}>
                <div style={styles.gridItem}>
                  <div style={styles.gridLabel}>Min Opening Balance</div>
                  <div style={styles.gridValue}>{formatCurrency(type.minimumOpeningBalance)}</div>
                </div>
                <div style={styles.gridItem}>
                  <div style={styles.gridLabel}>Min Balance</div>
                  <div style={styles.gridValue}>{formatCurrency(type.minimumBalance)}</div>
                </div>
                <div style={styles.gridItem}>
                  <div style={styles.gridLabel}>Daily Limit</div>
                  <div style={styles.gridValue}>{formatCurrency(type.defaultDailyLimit)}</div>
                </div>
                <div style={styles.gridItem}>
                  <div style={styles.gridLabel}>Interest Rate</div>
                  <div style={{ ...styles.gridValue, color: "#16a34a" }}>{type.baseInterestRate}% p.a.</div>
                </div>
                <div style={styles.gridItem}>
                  <div style={styles.gridLabel}>Monthly Fee</div>
                  <div style={styles.gridValue}>{formatCurrency(type.monthlyMaintenanceFee)}</div>
                </div>
                <div style={styles.gridItem}>
                  <div style={styles.gridLabel}>Interest Credit</div>
                  <div style={styles.gridValue}>{type.interestCreditFrequency}</div>
                </div>
              </div>

              <div style={{ marginTop: "10px", fontSize: "12px", color: "#6b7280" }}>
                Features: 
                {type.hasDebitCard && " 💳 Debit Card"}
                {type.hasChequeBook && " 📝 Cheque Book"}
                {type.allowOverdraft && ` 📉 Overdraft (${formatCurrency(type.maxOverdraftLimit)})`}
                {type.allowOnlineTransactions && " 🌐 Online"}
                {type.allowInternationalTransactions && " ✈️ International"}
              </div>

              <div style={styles.btnRow}>
                <button style={{ ...styles.btn, ...styles.primaryBtn }} onClick={() => handleEditAccountType(type)}>
                  ✏️ Edit
                </button>
                <button style={{ ...styles.btn, ...styles.warningBtn }} onClick={() => handleApplyToExisting(type.id, "limits")}>
                  📤 Apply Limits to Existing
                </button>
                <button style={{ ...styles.btn, ...styles.successBtn }} onClick={() => handleApplyToExisting(type.id, "interest")}>
                  📤 Apply Interest to Existing
                </button>
                <button 
                  style={{ ...styles.btn, ...(type.isActive ? styles.dangerBtn : styles.successBtn) }}
                  onClick={() => handleToggleStatus(type, "account")}
                >
                  {type.isActive ? "🚫 Deactivate" : "✅ Activate"}
                </button>
              </div>
            </div>
          ))}
        </>
      ) : (
        <>
          <div style={{ marginBottom: "15px" }}>
            <button style={{ ...styles.btn, ...styles.successBtn }} onClick={openCreateFdModal}>
              ➕ Create New FD Product
            </button>
          </div>

          {fdProducts.map(product => (
            <div key={product.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <div>
                  <div style={styles.cardTitle}>{product.productName}</div>
                  <div style={{ fontSize: "13px", color: "#6b7280" }}>{product.description}</div>
                  <div style={styles.statsRow}>
                    <span style={styles.stat}>📊 {product.activeDeposits} active deposits</span>
                    <span style={styles.stat}>💰 {formatCurrency(product.totalDeposited)} total</span>
                  </div>
                </div>
                <span style={{ ...styles.badge, ...(product.isActive ? styles.activeBadge : styles.inactiveBadge) }}>
                  {product.isActive ? "Active" : "Inactive"}
                </span>
              </div>

              <div style={styles.grid}>
                <div style={styles.gridItem}>
                  <div style={styles.gridLabel}>Tenor</div>
                  <div style={styles.gridValue}>{product.tenorMonths} Months</div>
                </div>
                <div style={styles.gridItem}>
                  <div style={styles.gridLabel}>Interest Rate</div>
                  <div style={{ ...styles.gridValue, color: "#16a34a" }}>{product.interestRate}% p.a.</div>
                </div>
                <div style={styles.gridItem}>
                  <div style={styles.gridLabel}>Min Amount</div>
                  <div style={styles.gridValue}>{formatCurrency(product.minimumAmount)}</div>
                </div>
                <div style={styles.gridItem}>
                  <div style={styles.gridLabel}>Early Withdrawal Penalty</div>
                  <div style={styles.gridValue}>{product.earlyWithdrawalPenalty}%</div>
                </div>
              </div>

              <div style={styles.btnRow}>
                <button style={{ ...styles.btn, ...styles.primaryBtn }} onClick={() => handleEditFdProduct(product)}>
                  ✏️ Edit
                </button>
                <button 
                  style={{ ...styles.btn, ...(product.isActive ? styles.dangerBtn : styles.successBtn) }}
                  onClick={() => handleToggleStatus(product, "fd")}
                >
                  {product.isActive ? "🚫 Deactivate" : "✅ Activate"}
                </button>
              </div>
            </div>
          ))}
        </>
      )}

      {/* Edit Account Type Modal */}
      {showEditModal && selectedItem && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>✏️ Edit {selectedItem.displayName}</h3>

            <div style={styles.formSection}>
              <div style={styles.sectionTitle}>📋 Basic Information</div>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Display Name</label>
                  <input type="text" style={styles.input} value={editForm.displayName || ""} onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Description</label>
                  <input type="text" style={styles.input} value={editForm.description || ""} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                </div>
              </div>
            </div>

            <div style={styles.formSection}>
              <div style={styles.sectionTitle}>💰 Balance Requirements</div>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Min Opening Balance (₦)</label>
                  <input type="number" style={styles.input} value={editForm.minimumOpeningBalance || 0} onChange={(e) => setEditForm({ ...editForm, minimumOpeningBalance: parseFloat(e.target.value) })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Min Balance (₦)</label>
                  <input type="number" style={styles.input} value={editForm.minimumBalance || 0} onChange={(e) => setEditForm({ ...editForm, minimumBalance: parseFloat(e.target.value) })} />
                </div>
              </div>
            </div>

            <div style={styles.formSection}>
              <div style={styles.sectionTitle}>📊 Transaction Limits</div>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Default Daily Limit (₦)</label>
                  <input type="number" style={styles.input} value={editForm.defaultDailyLimit || 0} onChange={(e) => setEditForm({ ...editForm, defaultDailyLimit: parseFloat(e.target.value) })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Single Transaction Limit (₦)</label>
                  <input type="number" style={styles.input} value={editForm.defaultSingleTransactionLimit || 0} onChange={(e) => setEditForm({ ...editForm, defaultSingleTransactionLimit: parseFloat(e.target.value) })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Max Daily Limit (₦)</label>
                  <input type="number" style={styles.input} value={editForm.maxDailyLimit || 0} onChange={(e) => setEditForm({ ...editForm, maxDailyLimit: parseFloat(e.target.value) })} />
                </div>
              </div>
            </div>

            <div style={styles.formSection}>
              <div style={styles.sectionTitle}>📈 Interest Settings</div>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Interest Rate (% p.a.)</label>
                  <input type="number" step="0.1" style={styles.input} value={editForm.baseInterestRate || 0} onChange={(e) => setEditForm({ ...editForm, baseInterestRate: parseFloat(e.target.value) })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Interest Credit Frequency</label>
                  <select style={styles.select} value={editForm.interestCreditFrequency || "Monthly"} onChange={(e) => setEditForm({ ...editForm, interestCreditFrequency: e.target.value })}>
                    <option value="Daily">Daily</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Yearly">Yearly</option>
                    <option value="AtMaturity">At Maturity</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={styles.formSection}>
              <div style={styles.sectionTitle}>💸 Fees</div>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Monthly Maintenance Fee (₦)</label>
                  <input type="number" style={styles.input} value={editForm.monthlyMaintenanceFee || 0} onChange={(e) => setEditForm({ ...editForm, monthlyMaintenanceFee: parseFloat(e.target.value) })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Below Min Balance Fee (₦)</label>
                  <input type="number" style={styles.input} value={editForm.belowMinimumBalanceFee || 0} onChange={(e) => setEditForm({ ...editForm, belowMinimumBalanceFee: parseFloat(e.target.value) })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Free Withdrawals/Month (-1 = unlimited)</label>
                  <input type="number" style={styles.input} value={editForm.freeWithdrawalsPerMonth ?? -1} onChange={(e) => setEditForm({ ...editForm, freeWithdrawalsPerMonth: parseInt(e.target.value) })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Excess Withdrawal Fee (₦)</label>
                  <input type="number" style={styles.input} value={editForm.excessWithdrawalFee || 0} onChange={(e) => setEditForm({ ...editForm, excessWithdrawalFee: parseFloat(e.target.value) })} />
                </div>
              </div>
            </div>

            <div style={styles.formSection}>
              <div style={styles.sectionTitle}>📉 Overdraft Settings</div>
              <div style={styles.formGrid}>
                <label style={styles.checkbox}>
                  <input type="checkbox" checked={editForm.allowOverdraft || false} onChange={(e) => setEditForm({ ...editForm, allowOverdraft: e.target.checked })} />
                  <span>Allow Overdraft</span>
                </label>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Max Overdraft Limit (₦)</label>
                  <input type="number" style={styles.input} value={editForm.maxOverdraftLimit || 0} onChange={(e) => setEditForm({ ...editForm, maxOverdraftLimit: parseFloat(e.target.value) })} disabled={!editForm.allowOverdraft} />
                </div>
              </div>
            </div>

            <div style={styles.formSection}>
              <div style={styles.sectionTitle}>✨ Features</div>
              <div style={styles.formGrid}>
                <label style={styles.checkbox}>
                  <input type="checkbox" checked={editForm.hasDebitCard || false} onChange={(e) => setEditForm({ ...editForm, hasDebitCard: e.target.checked })} />
                  <span>💳 Debit Card</span>
                </label>
                <label style={styles.checkbox}>
                  <input type="checkbox" checked={editForm.hasChequeBook || false} onChange={(e) => setEditForm({ ...editForm, hasChequeBook: e.target.checked })} />
                  <span>📝 Cheque Book</span>
                </label>
                <label style={styles.checkbox}>
                  <input type="checkbox" checked={editForm.allowOnlineTransactions || false} onChange={(e) => setEditForm({ ...editForm, allowOnlineTransactions: e.target.checked })} />
                  <span>🌐 Online Transactions</span>
                </label>
                <label style={styles.checkbox}>
                  <input type="checkbox" checked={editForm.allowInternationalTransactions || false} onChange={(e) => setEditForm({ ...editForm, allowInternationalTransactions: e.target.checked })} />
                  <span>✈️ International Transactions</span>
                </label>
              </div>
            </div>

            <div style={styles.formSection}>
              <div style={styles.sectionTitle}>👥 Eligibility</div>
              <div style={styles.formGrid}>
                <label style={styles.checkbox}>
                  <input type="checkbox" checked={editForm.availableForIndividuals || false} onChange={(e) => setEditForm({ ...editForm, availableForIndividuals: e.target.checked })} />
                  <span>Available for Individuals</span>
                </label>
                <label style={styles.checkbox}>
                  <input type="checkbox" checked={editForm.availableForCorporates || false} onChange={(e) => setEditForm({ ...editForm, availableForCorporates: e.target.checked })} />
                  <span>Available for Corporates</span>
                </label>
                <label style={styles.checkbox}>
                  <input type="checkbox" checked={editForm.requiresKYC || false} onChange={(e) => setEditForm({ ...editForm, requiresKYC: e.target.checked })} />
                  <span>Requires KYC</span>
                </label>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Min Age Requirement</label>
                  <input type="number" style={styles.input} value={editForm.minimumAgeRequirement || 18} onChange={(e) => setEditForm({ ...editForm, minimumAgeRequirement: parseInt(e.target.value) })} />
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button style={{ ...styles.btn, ...styles.secondaryBtn }} onClick={() => setShowEditModal(false)}>Cancel</button>
              <button style={{ ...styles.btn, ...styles.primaryBtn }} onClick={handleSaveAccountType} disabled={loading}>
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Account Type Modal */}
      {showCreateModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>➕ Create New Account Type</h3>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Account Type Code *</label>
                <input type="text" style={styles.input} placeholder="e.g., Premium" value={createForm.accountType || ""} onChange={(e) => setCreateForm({ ...createForm, accountType: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Display Name *</label>
                <input type="text" style={styles.input} placeholder="e.g., Premium Savings" value={createForm.displayName || ""} onChange={(e) => setCreateForm({ ...createForm, displayName: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Min Opening Balance (₦)</label>
                <input type="number" style={styles.input} value={createForm.minimumOpeningBalance || 0} onChange={(e) => setCreateForm({ ...createForm, minimumOpeningBalance: parseFloat(e.target.value) })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Daily Limit (₦)</label>
                <input type="number" style={styles.input} value={createForm.defaultDailyLimit || 1000000} onChange={(e) => setCreateForm({ ...createForm, defaultDailyLimit: parseFloat(e.target.value) })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Interest Rate (% p.a.)</label>
                <input type="number" step="0.1" style={styles.input} value={createForm.baseInterestRate || 0} onChange={(e) => setCreateForm({ ...createForm, baseInterestRate: parseFloat(e.target.value) })} />
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={{ ...styles.btn, ...styles.secondaryBtn }} onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button style={{ ...styles.btn, ...styles.successBtn }} onClick={handleCreateAccountType} disabled={loading}>
                {loading ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FD Product Modal */}
      {showFdModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>{selectedItem ? "✏️ Edit" : "➕ Create"} FD Product</h3>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Product Name *</label>
                <input type="text" style={styles.input} value={editForm.productName || ""} onChange={(e) => setEditForm({ ...editForm, productName: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Tenor (Months) *</label>
                <input type="number" style={styles.input} value={editForm.tenorMonths || 12} onChange={(e) => setEditForm({ ...editForm, tenorMonths: parseInt(e.target.value) })} disabled={!!selectedItem} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Interest Rate (% p.a.) *</label>
                <input type="number" step="0.1" style={styles.input} value={editForm.interestRate || 0} onChange={(e) => setEditForm({ ...editForm, interestRate: parseFloat(e.target.value) })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Min Amount (₦) *</label>
                <input type="number" style={styles.input} value={editForm.minimumAmount || 50000} onChange={(e) => setEditForm({ ...editForm, minimumAmount: parseFloat(e.target.value) })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Max Amount (₦)</label>
                <input type="number" style={styles.input} value={editForm.maximumAmount || ""} onChange={(e) => setEditForm({ ...editForm, maximumAmount: e.target.value ? parseFloat(e.target.value) : null })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Early Withdrawal Penalty (%)</label>
                <input type="number" style={styles.input} value={editForm.earlyWithdrawalPenalty || 50} onChange={(e) => setEditForm({ ...editForm, earlyWithdrawalPenalty: parseFloat(e.target.value) })} />
              </div>
              <label style={styles.checkbox}>
                <input type="checkbox" checked={editForm.allowEarlyWithdrawal || false} onChange={(e) => setEditForm({ ...editForm, allowEarlyWithdrawal: e.target.checked })} />
                <span>Allow Early Withdrawal</span>
              </label>
              <label style={styles.checkbox}>
                <input type="checkbox" checked={editForm.autoRenewByDefault || false} onChange={(e) => setEditForm({ ...editForm, autoRenewByDefault: e.target.checked })} />
                <span>Auto-Renew by Default</span>
              </label>
            </div>
            <div style={styles.modalFooter}>
              <button style={{ ...styles.btn, ...styles.secondaryBtn }} onClick={() => setShowFdModal(false)}>Cancel</button>
              <button style={{ ...styles.btn, ...styles.primaryBtn }} onClick={handleSaveFdProduct} disabled={loading}>
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

